import React from 'react';
import { Route } from 'react-router-dom';
import { RouteWrapper } from './RouteWrapper';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { OnboardingGuard } from '@/components/OnboardingGuard';
import {
  LazyDashboard,
  LazyAdminProperties,
  LazyPropertyForm,
  LazyPropertyEdit,
  LazyLeads,
  LazyLeadsKanban,
  LazyLeadDetail,
  LazyAgendamentos,
  LazyCustomization,
  LazyMarketing,
  LazySettings,
  LazyProfileSettings,
  LazyDomainSettings,
  LazySubscriptionSettings,
  LazyIntegrationsSettings,
  LazyFinanceiro,
  LazyPublicProfile,
  LazyAdminLayout,
  LazyAdminAccountSettings,
  LazyBusinessSettings,
  LazyTeamSettings,
  LazyGeradorPropostas,
  LazyPropostas,
  LazyReferrals,
  LazySiteSetupWizard,
  LazyOAuthCallback,
} from '@/components/LazyComponents';
import { Navigate, Outlet } from 'react-router-dom';

import { RoleGuard, UserRole } from '@/components/RoleGuard';

/**
 * Wrapper para rotas admin com proteção e layout
 */
const AdminRouteWrapper: React.FC<{ 
  children: React.ReactNode; 
  withSync?: boolean;
  allowedRoles?: UserRole[];
}> = ({
  children,
  withSync = false,
  allowedRoles = ['owner', 'admin', 'member'] // Por padrão, todos acessam
}) => (
  <ProtectedRoute>
    <OnboardingGuard>
      <RouteWrapper withTenant withSync={withSync}>
        <RoleGuard allowedRoles={allowedRoles}>
          <React.Suspense fallback={<div className="min-h-screen bg-background flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>}>
            <LazyAdminLayout>{children}</LazyAdminLayout>
          </React.Suspense>
        </RoleGuard>
      </RouteWrapper>
    </OnboardingGuard>
  </ProtectedRoute>
);

export const AdminRoutes = () => {
  return (
    <>
      {/* Dashboard */}
      <Route
        path="/dashboard"
        element={
          <AdminRouteWrapper withSync>
            <LazyDashboard />
          </AdminRouteWrapper>
        }
      />

      <Route
        path="/criar-site"
        element={
          <AdminRouteWrapper withSync>
            <LazySiteSetupWizard />
          </AdminRouteWrapper>
        }
      />

      {/* Properties Management */}
      <Route
        path="/imoveis"
        element={
          <AdminRouteWrapper>
            <LazyAdminProperties />
          </AdminRouteWrapper>
        }
      />

      <Route
        path="/imoveis/novo"
        element={
          <AdminRouteWrapper>
            <LazyPropertyForm />
          </AdminRouteWrapper>
        }
      />

      <Route
        path="/imoveis/editar/:id"
        element={
          <AdminRouteWrapper>
            <LazyPropertyEdit />
          </AdminRouteWrapper>
        }
      />

      {/* Leads Management */}
      <Route
        path="/leads"
        element={
          <AdminRouteWrapper withSync>
            <LazyLeads />
          </AdminRouteWrapper>
        }
      />

      <Route
        path="/leads/kanban"
        element={
          <AdminRouteWrapper withSync>
            <LazyLeadsKanban />
          </AdminRouteWrapper>
        }
      />

      <Route
        path="/leads/:leadId"
        element={
          <AdminRouteWrapper withSync>
            <LazyLeadDetail />
          </AdminRouteWrapper>
        }
      />

      {/* Appointments */}
      <Route
        path="/agendamentos"
        element={
          <AdminRouteWrapper withSync>
            <LazyAgendamentos />
          </AdminRouteWrapper>
        }
      />

      {/* Marketing */}
      <Route
        path="/marketing"
        element={
          <AdminRouteWrapper allowedRoles={['owner', 'admin']}>
            <LazyMarketing />
          </AdminRouteWrapper>
        }
      />

      {/* Financial Route */}
      <Route
        path="/financeiro"
        element={<Navigate to="/financeiro/visao-geral" replace />}
      />
      <Route
        path="/financeiro/:tab"
        element={
          <AdminRouteWrapper allowedRoles={['owner', 'admin']}>
            <LazyFinanceiro />
          </AdminRouteWrapper>
        }
      />

      {/* Tools Route */}
      <Route
        path="/propostas"
        element={
          <AdminRouteWrapper>
            <LazyPropostas />
          </AdminRouteWrapper>
        }
      />

      <Route
        path="/gerador-propostas"
        element={
          <AdminRouteWrapper>
            <LazyGeradorPropostas />
          </AdminRouteWrapper>
        }
      />

      {/* Settings Routes - Nova Estrutura Unificada */}
      <Route
        path="/configuracoes"
        element={
          <AdminRouteWrapper>
            <Outlet />
          </AdminRouteWrapper>
        }
      >
        {/* Página inicial do Hub de Configurações */}
        <Route index element={<React.Suspense fallback={<div className="p-8"><div className="animate-pulse h-64 bg-muted rounded-xl"></div></div>}><LazySettings /></React.Suspense>} />
        
        {/* Seções de configurações */}
        {/* /conta é livre para corretores editarem seus próprios perfis */}
        <Route path="conta" element={<React.Suspense fallback={<div className="p-8"><div className="animate-pulse h-64 bg-muted rounded-xl"></div></div>}><LazyAdminAccountSettings /></React.Suspense>} />
        
        {/* As demais configurações são exclusivas do administrador */}
        <Route path="negocio" element={<RoleGuard allowedRoles={['owner', 'admin']}><React.Suspense fallback={<div className="p-8"><div className="animate-pulse h-64 bg-muted rounded-xl"></div></div>}><LazyBusinessSettings /></React.Suspense></RoleGuard>} />
        <Route path="site" element={<RoleGuard allowedRoles={['owner', 'admin']}><React.Suspense fallback={<div className="p-8"><div className="animate-pulse h-64 bg-muted rounded-xl"></div></div>}><LazySiteSetupWizard /></React.Suspense></RoleGuard>} />
        <Route path="site/domain" element={<Navigate to="/configuracoes/site?step=5" replace />} />
        <Route path="site/*" element={<Navigate to="/configuracoes/site" replace />} />
        <Route path="equipe" element={<RoleGuard allowedRoles={['owner', 'admin']}><React.Suspense fallback={<div className="p-8"><div className="animate-pulse h-64 bg-muted rounded-xl"></div></div>}><LazyTeamSettings /></React.Suspense></RoleGuard>} />
        <Route path="integracoes" element={<LazyIntegrationsSettings />} />
        <Route path="integracoes/callback" element={<LazyOAuthCallback />} />
        <Route path="google-calendar-callback" element={<LazyOAuthCallback />} />
        <Route path="plano" element={<RoleGuard allowedRoles={['owner', 'admin']}><LazySubscriptionSettings /></RoleGuard>} />
        <Route path="indicacoes" element={<RoleGuard allowedRoles={['owner', 'admin']}><React.Suspense fallback={<div className="p-8"><div className="animate-pulse h-64 bg-muted rounded-xl"></div></div>}><LazyReferrals /></React.Suspense></RoleGuard>} />
      </Route>

      {/* Redirects para compatibilidade com rotas antigas */}
      <Route
        path="/personalizacao"
        element={<Navigate to="/configuracoes/site" replace />}
      />
      <Route
        path="/configuracoes/perfil"
        element={<Navigate to="/configuracoes/conta" replace />}
      />
      <Route
        path="/configuracoes/dominio"
        element={<Navigate to="/configuracoes/site" replace />}
      />
      <Route
        path="/configuracoes/assinatura"
        element={<Navigate to="/configuracoes/plano" replace />}
      />
    </>
  );
};
